import { buildHeader, buildParagraph, buildNewlines, createTableHeaderCell, createTableCell } from "./helpers";
import { Table, TableRow } from "docx";
import { getStudentSkillsMastery } from "@/services/weeklyPlanner/query/getStudentSkillsMastery";
import { fetchCurriculumLessonPlansData } from "@/services/query/curriculumLessonPlansAPI";
import { getMasteryByUnit } from "@/services/query/skillsMasteryAPI";
import { getUserData } from "@/services/API";
import { Grade } from "@/enums";
import { getSchoolYearDates } from "./utils/schoolYearUtils";
import { getMasteryColor, getMasteryStatus } from "../../utilities/masteryUtils";
import { formatGradeDisplay, formatPercentage, getDisplayName } from "./utils/formatUtils";



const mapGradeToEnum = (gradeInput) => {
  // Handle various grade formats and convert to Grade enum
  const gradeStr = String(gradeInput).toLowerCase();
  
  if (gradeStr === "0" || gradeStr === "k" || gradeStr === "kindergarten") return Grade.KINDERGARTEN;
  if (gradeStr === "1" || gradeStr === "first" || gradeStr === "first_grade") return Grade.FIRST;
  if (gradeStr === "2" || gradeStr === "second" || gradeStr === "second_grade") return Grade.SECOND;
  if (gradeStr === "3" || gradeStr === "third" || gradeStr === "third_grade") return Grade.THIRD;
  if (gradeStr === "4" || gradeStr === "fourth" || gradeStr === "fourth_grade") return Grade.FOURTH;
  if (gradeStr === "5" || gradeStr === "fifth" || gradeStr === "fifth_grade") return Grade.FIFTH;
  if (gradeStr === "6" || gradeStr === "sixth" || gradeStr === "sixth_grade") return Grade.SIXTH;
  if (gradeStr === "7" || gradeStr === "seventh" || gradeStr === "seventh_grade") return Grade.SEVENTH;
  if (gradeStr === "8" || gradeStr === "eighth" || gradeStr === "eighth_grade") return Grade.EIGHTH;
  if (gradeStr === "9" || gradeStr === "ninth" || gradeStr === "ninth_grade") return Grade.NINTH;
  if (gradeStr === "-1" || gradeStr === "pk" || gradeStr === "pre-k" || gradeStr === "prekindergarten") return Grade.PK;
  
  // Default to kindergarten if we can't parse the grade
  return Grade.KINDERGARTEN;
};

const isVocabularySkill = (skill) => {
  const skillName = (skill.skillName || skill.displayName || "").toLowerCase();
  const skillId = (skill.skillId || "").toLowerCase();
  
  // Check if skill is vocabulary-related
  return skillName.includes("vocabulary") || 
         skillName.includes("vocab") || 
         skillId.includes("vocabulary") || 
         skillId.includes("vocab") ||
         skillId.startsWith("v.") ||
         skillId.includes(".v.");
};

const extractUnitsFromCurriculum = (curriculumResponse) => {
  const units = [];
  
  if (!curriculumResponse || !curriculumResponse.getCurriculumLessonPlans) {
    return units;
  }

  curriculumResponse.getCurriculumLessonPlans.forEach(lessonPlan => {
    if (lessonPlan.tracks && Array.isArray(lessonPlan.tracks)) {
      lessonPlan.tracks.forEach(track => {
        if (track.units && Array.isArray(track.units)) {
          track.units.forEach(unit => {
            // Avoid duplicates
            if (!units.find(u => u.id === unit.id)) {
              units.push({
                id: unit.id,
                displayName: unit.displayName || `Unit ${unit.sequence}`,
                sequence: unit.sequence,
                trackName: track.displayName || `Track ${track.sequence}`,
                curriculumName: lessonPlan.displayName
              });
            }
          });
        }
      });
    }
  });
  
  return units;
};

const createUnitsTable = (unitsWithMastery) => {
  if (!unitsWithMastery || unitsWithMastery.length === 0) {
    return buildParagraph("No units found in curriculum.", { italics: true });
  }

  const headers = ["Unit", "Track/Curriculum", "Average Mastery"];
  const headerRow = new TableRow({
    children: headers.map(header => createTableHeaderCell(header))
  });
  
  const dataRows = unitsWithMastery.map(unit => 
    new TableRow({
      children: [
        createTableCell({ 
          text: getDisplayName(unit, "Unknown Unit")
        }),
        createTableCell({ 
          text: `${unit.trackName || "Unknown Track"} - ${unit.curriculumName || "Unknown Curriculum"}`
        }),
        createTableCell({ 
          text: formatPercentage(unit.averageMastery)
        })
      ]
    })
  );

  return new Table({
    rows: [headerRow, ...dataRows],
    width: {
      size: 100,
      type: "pct"
    }
  });
};

const extractVocabularySkillsFromFullCurriculum = async (studentData) => {
  const vocabularySkills = [];
  
  try {
    // Get user data to make API call
    const userData = getUserData();
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    // School year typically starts in August, so if we're before August, use previous year
    const schoolYear = currentDate.getMonth() < 7 ? currentYear - 1 : currentYear;
    
    // Fetch the full school year curriculum for the student's grade
    const gradeEnum = mapGradeToEnum(studentData.grade);
    
    const curriculumResponse = await fetchCurriculumLessonPlansData({
      districtId: userData.districtId,
      grade: gradeEnum,
      schoolYear: schoolYear,
      filter: {
        entityTypes: "DISTRICT"
      }
    });

    if (!curriculumResponse || !curriculumResponse.getCurriculumLessonPlans) {
      return vocabularySkills;
    }

    // Extract vocabulary skills from ALL curriculum lesson plans
    curriculumResponse.getCurriculumLessonPlans.forEach(lessonPlan => {
      if (lessonPlan.tracks && Array.isArray(lessonPlan.tracks)) {
        lessonPlan.tracks.forEach(track => {
          if (track.units && Array.isArray(track.units)) {
            track.units.forEach(unit => {
              if (unit.lessons && Array.isArray(unit.lessons)) {
                unit.lessons.forEach(lesson => {
                  if (lesson.skills && Array.isArray(lesson.skills)) {
                    lesson.skills
                      .filter(skill => skill.skillType === "FOCUS") // Only focus skills
                      .filter(isVocabularySkill) // Only vocabulary skills
                      .forEach(skill => {
                        // Avoid duplicates
                        if (!vocabularySkills.find(vs => vs.skillId === skill.skillId)) {
                          vocabularySkills.push({
                            ...skill,
                            lessonName: lesson.displayName || `Lesson ${lesson.sequence}`,
                            unitName: unit.displayName || `Unit ${unit.sequence}`,
                            trackName: track.displayName || `Track ${track.sequence}`,
                            curriculumName: lessonPlan.displayName,
                            lessonId: lesson.id
                          });
                        }
                      });
                  }
                });
              }
            });
          }
        });
      }
    });

    return vocabularySkills;
    
  } catch (error) {
    console.error("Error fetching full curriculum for vocabulary skills:", error);
    return vocabularySkills; // Return empty array on error
  }
};

const createVocabularyProgressTable = (vocabularySkillsWithMastery) => {
  if (!vocabularySkillsWithMastery || vocabularySkillsWithMastery.length === 0) {
    return buildParagraph("No vocabulary skills found in curriculum.", { italics: true });
  }

  const headers = ["Skill", "Current Mastery"];
  const headerRow = new TableRow({
    children: headers.map(header => createTableHeaderCell(header))
  });
  
  const dataRows = vocabularySkillsWithMastery.map(skill => {
    const masteryColor = getMasteryColor(skill.masteryPacing);
    
    return new TableRow({
      children: [
        createTableCell({ 
          text: getDisplayName(skill, "Unknown Skill")
        }),
        createTableCell({ 
          text: getMasteryStatus(skill.masteryPacing),
          shadingColor: masteryColor
        })
      ]
    });
  });

  return new Table({
    rows: [headerRow, ...dataRows],
    width: {
      size: 100,
      type: "pct"
    },
    columnWidths: [6500, 2500]
  });
};

const createSectionProgress = async (studentData, weeklyPlan) => {
  try {
    // Get user data and calculate date range first
    const userData = getUserData();
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const schoolYear = currentDate.getMonth() < 7 ? currentYear - 1 : currentYear;
    
    // Get school year start date from calendaring service
    const { startDate: yearStartDate } = await getSchoolYearDates();

    // Fetch curriculum data once
    const gradeEnum = mapGradeToEnum(studentData.grade);
    const curriculumResponse = await fetchCurriculumLessonPlansData({
      districtId: userData.districtId,
      grade: gradeEnum,
      schoolYear: schoolYear,
      filter: {
        entityTypes: "DISTRICT"
      }
    });

    if (!curriculumResponse || !curriculumResponse.getCurriculumLessonPlans) {
      return [
        buildHeader(`${studentData.firstName}'s Core Knowledge Progress`, 3),
        buildParagraph("No curriculum data available for this grade level.", { italics: true }),
        ...buildNewlines(4),
        buildHeader(`${studentData.firstName}'s Vocabulary Progress`, 3),
        buildParagraph("No curriculum data available for this grade level.", { italics: true }),
        ...buildNewlines(4),
      ];
    }

    // Extract units from curriculum
    const units = extractUnitsFromCurriculum(curriculumResponse);
    
    // Get mastery data for units
    let unitsWithMastery = [...units];
    
    if (units.length > 0) {
      try {
        // Get mastery data for all units at once
        const unitIds = units.map(unit => unit.id);
        
        const masteryData = await getMasteryByUnit(
          unitIds,
          [studentData.id],
          yearStartDate.toISOString().split("T")[0],
          currentDate.toISOString().split("T")[0]
        );

        // Map mastery data back to units
        unitsWithMastery = units.map(unit => {
          const unitMastery = masteryData.find(mastery => mastery.unitId === unit.id);
          return {
            ...unit,
            averageMastery: unitMastery?.averageMastery
          };
        });
      } catch (error) {
        console.error("[Core Knowledge] Error fetching unit mastery data:", error);
        // Continue with units but no mastery data
      }
          }

    // Extract vocabulary skills from curriculum
    const vocabularySkills = [];
    curriculumResponse.getCurriculumLessonPlans.forEach(lessonPlan => {
      if (lessonPlan.tracks && Array.isArray(lessonPlan.tracks)) {
        lessonPlan.tracks.forEach(track => {
          if (track.units && Array.isArray(track.units)) {
            track.units.forEach(unit => {
              if (unit.lessons && Array.isArray(unit.lessons)) {
                unit.lessons.forEach(lesson => {
                  if (lesson.skills && Array.isArray(lesson.skills)) {
                    lesson.skills
                      .filter(skill => skill.skillType === "FOCUS")
                      .filter(isVocabularySkill)
                      .forEach(skill => {
                        if (!vocabularySkills.find(vs => vs.skillId === skill.skillId)) {
                          vocabularySkills.push({
                            ...skill,
                            lessonName: lesson.displayName || `Lesson ${lesson.sequence}`,
                            unitName: unit.displayName || `Unit ${unit.sequence}`,
                            trackName: track.displayName || `Track ${track.sequence}`,
                            curriculumName: lessonPlan.displayName,
                            lessonId: lesson.id
                          });
                        }
                      });
                  }
                });
              }
            });
          }
        });
      }
    });

    if (vocabularySkills.length === 0 && units.length === 0) {
      return [
        buildHeader(`${studentData.firstName}'s Core Knowledge Progress`, 3),
        buildParagraph("No curriculum data found for this grade level.", { italics: true }),
        ...buildNewlines(4),
        buildHeader(`${studentData.firstName}'s Vocabulary Progress`, 3),
        buildParagraph("No vocabulary focus skills found in curriculum.", { italics: true }),
        ...buildNewlines(4),
      ];
    }

    // Get mastery data for vocabulary skills
    let vocabularySkillsWithMastery = [...vocabularySkills];
    
    if (vocabularySkills.length > 0) {
      try {
        const skillIds = vocabularySkills.map(skill => skill.skillId).filter(Boolean);
        
        if (skillIds.length > 0) {
          const masteryData = await getStudentSkillsMastery({
            studentId: [studentData.id],
            skillId: skillIds,
            dateFrom: yearStartDate.toISOString().split("T")[0],
            dateTo: currentDate.toISOString().split("T")[0]
          });



          // Map mastery data to skills
          vocabularySkillsWithMastery = vocabularySkills.map(skill => {
            const studentMastery = masteryData.studentSkillsMastery?.find(
              mastery => mastery.records?.find(record => record.skillId === skill.skillId)
            );
            
            const skillRecord = studentMastery?.records?.find(record => record.skillId === skill.skillId);
            
            return {
              ...skill,
              masteryPacing: skillRecord?.pacing || null
            };
          });
        }
      } catch (error) {
        console.error("Error fetching vocabulary mastery data:", error);
        // Continue with skills but no mastery data
      }
    }

    return [
      // Core Knowledge Progress Header
      buildHeader(`${studentData.firstName}'s Core Knowledge Progress`, 3),
      // Core Knowledge Progress Summary
      buildParagraph(
        `${studentData.firstName} is progressing through the core knowledge curriculum with ${units.length} units across the full school year.`,
        { italics: false }
      ),
      // Core Knowledge Progress Table - Units with Mastery
      ...buildNewlines(2),
      createUnitsTable(unitsWithMastery),
      ...buildNewlines(4),
      // Vocabulary Progress Header
      buildHeader(`${studentData.firstName}'s Vocabulary Progress`, 3),
      // Summary of vocabulary progress
      buildParagraph(
        vocabularySkills.length > 0 
          ? `Here's how ${studentData.firstName} is doing with ${formatGradeDisplay(studentData.grade)} ${vocabularySkills[0]?.trackName || 'curriculum'} vocabulary.`
          : `No vocabulary focus skills found in curriculum for ${studentData.firstName}.`,
        { bold: true }
      ),
      // Vocabulary Progress Table (only here)
      ...buildNewlines(2),
      vocabularySkills.length > 0 
        ? createVocabularyProgressTable(vocabularySkillsWithMastery)
        : buildParagraph("No vocabulary skills to display.", { italics: true }),
      ...buildNewlines(4),
    ];
    
  } catch (error) {
    console.error("Error creating vocabulary progress section:", error);
    return [
      buildHeader(`${studentData.firstName}'s Core Knowledge Progress`, 3),
      buildParagraph("Unable to load full curriculum vocabulary data at this time.", { italics: true }),
      ...buildNewlines(4),
      buildHeader(`${studentData.firstName}'s Vocabulary Progress`, 3),
      buildParagraph("Unable to load full curriculum vocabulary data at this time.", { italics: true }),
      ...buildNewlines(4),
    ];
  }
};

export default createSectionProgress;
